import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ClientDashboard from "@/pages/ClientDashboard";
import NewTicket from "@/pages/NewTicket";
import TicketDetail from "@/pages/TicketDetail";
import Credits from "@/pages/Credits";
import Profile from "@/pages/Profile";
import PaymentResult from "@/pages/PaymentResult";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCalendar from "@/pages/AdminCalendar";
import AdminQuotes from "@/pages/AdminQuotes";
import Landing from "@/pages/Landing";

function Loader() {
  return (
    <div className="min-h-screen grid place-items-center" data-testid="app-loader">
      <div className="rev-loader" />
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, login } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) {
      return;
    }
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    api
      .post("/auth/google/session", { session_id: sid })
      .then(({ data }) => {
        login(data);
        navigate(data.role === "admin" ? "/admin" : "/dashboard", { replace: true });
      })
      .catch(() => {
        setUser(false);
        navigate("/login", { replace: true });
      });
  }, [navigate, setUser, login]);
  return <Loader />;
}

function Protected({ children, admin }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <AppShell>{children}</AppShell>;
}

function ClientHome() {
  const { user } = useAuth();
  return user?.role === "admin" ? <Navigate to="/admin" replace /> : <ClientDashboard />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/registrati" element={<Register />} />
      <Route path="/dashboard" element={<Protected><ClientHome /></Protected>} />
      <Route path="/nuova-richiesta" element={<Protected><NewTicket /></Protected>} />
      <Route path="/ticket/:id" element={<Protected><TicketDetail /></Protected>} />
      <Route path="/crediti" element={<Protected><Credits /></Protected>} />
      <Route path="/profilo" element={<Protected><Profile /></Protected>} />
      <Route path="/payment/success" element={<PaymentResult success />} />
      <Route path="/payment/cancel" element={<PaymentResult />} />
      <Route path="/admin" element={<Protected admin><AdminDashboard /></Protected>} />
      <Route path="/admin/calendario" element={<Protected admin><AdminCalendar /></Protected>} />
      <Route path="/admin/preventivi" element={<Protected admin><AdminQuotes /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster theme="dark" position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
